import React, { useState, useEffect } from 'react';
import { Container, Button, Spinner, Pagination, Collapse } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import { FaSync, FaDownload, FaChevronUp, FaPlus } from 'react-icons/fa';
import { useCrudOperations } from '../hooks/useCrudOperations';
import { useTableOperations } from '../hooks/useTableOperations';
import GenericForm from './GenericForm';
import GenericTable from './GenericTable';
import SearchAndFilters from './SearchAndFilters';
import GenericViewModal from './GenericViewModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { exportToCSV } from '../utils/csvExport';
import { toast } from 'react-toastify';
import axios from '../api/axios';
import Cookies from 'js-cookie';

const GenericCrudPage = ({
  title,
  pluralTitle,
  config,
  formFields,
  tableColumns,
  emptyIcon,
  searchPlaceholder,
  csvHeaders,
  csvFilename,
  transformPayload,
  transformToForm,
  getViewFields,
  onAddClick,       
  editFormFields, 
  onEdit,          
  headerContent,   
  fetchItemForView, 
  fetchItemForEdit, 
  hideGlobalSearch, 
}) => {
  
  const displayTitle = pluralTitle || (title.endsWith('s') ? title : `${title}s`);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { globalSearch, setGlobalSearch } = useOutletContext() || {};
  const crud = useCrudOperations(config);
  const table = useTableOperations(crud.items, tableColumns[0]?.key);
  useEffect(() => {
    if (crud.success) {
      toast.success(crud.success);
      crud.setSuccess(''); 
    }
    if (crud.error) {
      toast.error(crud.error);
      crud.setError(''); 
    }
  }, [crud.success, crud.error, crud.setSuccess, crud.setError]);
  const fieldsForForm = crud.editId && editFormFields ? editFormFields : formFields;
  useEffect(() => {
    if (globalSearch !== undefined && table.search !== globalSearch) {
      table.setSearch(globalSearch);
    }
  }, [globalSearch]);
  const handleLocalSearchChange = (value) => {
    table.setSearch(value); 
    if (setGlobalSearch) {
      setGlobalSearch(value); 
    }
  };

  const handleSubmit = async (e) => {
    const success = await crud.handleSubmit(e, transformPayload);
    if (success) setIsFormVisible(false);
  };
  const handleEdit = async (item) => {
    if (onEdit) {
      onEdit(item, () => setIsFormVisible(true));
    } else if (fetchItemForEdit) {
      const fullItem = await fetchItemForEdit(item);
      if (fullItem) {
        crud.handleEdit(fullItem, transformToForm);
        setIsFormVisible(true);
      }
    } else {
      crud.handleEdit(item, transformToForm);
      setIsFormVisible(true);
    }
  };
  
  const handleCancelForm = () => {
    crud.resetForm();
    setIsFormVisible(false);
  };

  const handleAdd = () => {
      if(onAddClick) {
          onAddClick();
      }
      setIsFormVisible(true);
  }

  const handleExport = () => {
    exportToCSV(table.filteredAndSortedItems, csvHeaders, csvFilename);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    const success = await crud.handleDelete(selectedItem);
    if (success) {
      setShowDeleteModal(false);
      setSelectedItem(null);
    }
  };

  const getItemNameForDelete = (item) => {
    if (!item) return '';
    if (item.exName) return item.exName;
    if (item.name) return item.name;
    if (item.exComponentName) return item.exComponentName;
    if (item.exCategoryName) return item.exCategoryName;
    if (item.firstName) return `${item.firstName} ${item.lastName || ''}`.trim();
    return `${title} ID: ${item.id}`;
  };
  const handleToggleStatus = async (item) => {
    if (!config.endpoints.update) {
      toast.error("Update functionality is not configured for this page.");
      return;
    }

    const toastId = toast.loading(`Updating status for ${getItemNameForDelete(item)}...`);

    try {
      const currentIsActive = item.exIsActive === 1 || item.exIsActive === true;
      const updatedItemForForm = { ...item, exIsActive: !currentIsActive };
      const formState = transformToForm(updatedItemForForm);
      const payload = transformPayload(formState, item.id, 1); 

      const token = Cookies.get('token');
      const updateUrl = `${config.baseUrl}${config.endpoints.update}`;

      const response = await axios.post(updateUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status === "1") {
        toast.update(toastId, { render: "Status updated successfully!", type: "success", isLoading: false, autoClose: 3000 });
        crud.fetchItems(); 
      } else {
        throw new Error(response.data.message || "Failed to update status.");
      }
    } catch (error) {
      toast.update(toastId, { render: error.message || "An error occurred.", type: "error", isLoading: false, autoClose: 5000 });
    }
    
  };
  

  return (
  
    <Container fluid className="py-4 px-lg-5 bg-light-subtle">
      {headerContent && <div className="mb-4">{headerContent}</div>}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bolder page-title">{displayTitle}</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={crud.fetchItems} disabled={crud.loading}>
            <FaSync className={crud.loading ? 'fa-spin' : ''} /> Refresh
          </Button>
          <Button variant="outline-success" onClick={handleExport}>
            <FaDownload /> Export CSV
          </Button>
          <Button
            variant="primary"
            className="btn-gradient"
            onClick={isFormVisible ? handleCancelForm : handleAdd}
          >
            {isFormVisible ? (
              <><FaChevronUp className="me-1" /> Hide Form</>
            ) : (
              <><FaPlus className="me-1" /> Add {title}</>
            )}
          </Button>
        </div>
      </div>

      <Collapse in={isFormVisible}>
        <div>
          <GenericForm
            title={title}
            onSubmit={handleSubmit}
            form={crud.form}
            handleChange={crud.handleChange}
            formErrors={crud.formErrors}
            submitting={crud.submitting}
            editId={crud.editId}
            onCancel={handleCancelForm}
            fields={fieldsForForm}
          />
        </div>
      </Collapse>

      <SearchAndFilters
        search={table.search} 
        setSearch={handleLocalSearchChange} 
        statusFilter={table.statusFilter}
        setStatusFilter={table.setStatusFilter}
        itemsPerPage={table.itemsPerPage}
        setItemsPerPage={table.setItemsPerPage}
        startIndex={table.startIndex}
        totalItems={table.filteredAndSortedItems.length}
        searchPlaceholder={searchPlaceholder}
        showSearchInput={!hideGlobalSearch}
      />

      {crud.loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading {displayTitle}...</p>
        </div>
      ) : (
        <>
          <GenericTable
            items={table.paginatedItems}
            columns={tableColumns}
            onSort={table.handleSort}
            onEdit={handleEdit}
            onView={async (item) => {
              if (fetchItemForView) {
                const fullItem = await fetchItemForView(item);
                if (fullItem) {
                  setSelectedItem(fullItem);
                  setShowViewModal(true);
                }
              } else {
                setSelectedItem(item);
                setShowViewModal(true);
              }
            }}
            onDelete={config.endpoints.delete ? handleDeleteClick : null}
            toggleStatus={handleToggleStatus}
            sortField={table.sortField}
            sortDirection={table.sortDirection}
            emptyIcon={emptyIcon}
            emptyTitle={`No ${displayTitle} Found`}
          />
          {table.totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First onClick={() => table.setCurrentPage(1)} disabled={table.currentPage === 1} />
                <Pagination.Prev onClick={() => table.setCurrentPage(c => Math.max(1, c - 1))} disabled={table.currentPage === 1} />
                <Pagination.Item active>{table.currentPage} of {table.totalPages}</Pagination.Item>
                <Pagination.Next onClick={() => table.setCurrentPage(c => Math.min(table.totalPages, c + 1))} disabled={table.currentPage === table.totalPages} />
                <Pagination.Last onClick={() => table.setCurrentPage(table.totalPages)} disabled={table.currentPage === table.totalPages} />
              </Pagination>
            </div>
          )}
        </>
      )}

      <GenericViewModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        item={selectedItem}
        title={title}
        fields={getViewFields ? getViewFields(selectedItem) : []}
      />
      
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        itemName={getItemNameForDelete(selectedItem)}
        submitting={crud.submitting}
      />
    </Container>
  );
};

export default GenericCrudPage;
